import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useZionFormContext } from '@fs/zion-form'
import { getNextUuid } from '@fs/zion-sls'
import { trackEvent } from '@fs/zion-analytics'
import AddRemoveFieldsDialog from '../../shared/components/AddRemoveFieldsDialog'

const factOptionShape = PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })

/**
 * Add/Remove Additional Facts dialog. Checks are staged; Save appends the checked facts and removes
 * the unchecked ones, value included.
 *
 * @param {Object} props
 * @param {Object} props.overlay - Return value of useOverlay.
 * @param {{commonFacts: Array<{value: string, label: string}>, otherFacts: Array<{value: string, label: string}>}} props.factOptions - Picker options from getAdditionalFactOptions.
 * @param {Object} props.fieldArrayMethods - useFieldArray methods for 'additionalFacts'.
 * @returns {JSX.Element}
 */
const AdditionalFactsDialog = ({ overlay, factOptions, fieldArrayMethods }) => {
  const [t] = useTranslation()
  const { getValues, formState } = useZionFormContext()

  const groups = useMemo(
    () => [
      { label: t('additional-facts-card.common-facts', 'Common Facts'), options: factOptions.commonFacts },
      { label: t('additional-facts-card.other-facts', 'Other Facts'), options: factOptions.otherFacts },
    ],
    [factOptions, t]
  )

  // Read on every render so the dialog opens with the card's current facts checked.
  const currentFacts = getValues('additionalFacts') || []
  const initialChecked = currentFacts.map((fact) => fact.key)

  const handleSave = (checked) => {
    const labels = new Map(groups.flatMap((group) => group.options.map((option) => [option.value, option.label])))
    const facts = getValues('additionalFacts') || []
    const presentKeys = new Set(facts.map((fact) => fact.key))

    // Facts whose type isn't offered (non-editable ones) are never removed here.
    const removedIndexes = facts.flatMap((fact, index) =>
      labels.has(fact.key) && !checked.has(fact.key) ? [index] : []
    )
    const removedKeys = [...new Set(removedIndexes.map((index) => facts[index].key))]
    const addedKeys = [...labels.keys()].filter((key) => checked.has(key) && !presentKeys.has(key))

    if (removedIndexes.length > 0) fieldArrayMethods.remove(removedIndexes)
    if (addedKeys.length > 0) {
      fieldArrayMethods.append(
        addedKeys.map(
          (key) =>
            // Re-adding a fact removed earlier brings back the saved one, so save doesn't delete and recreate it.
            formState.defaultValues?.additionalFacts?.find((fact) => fact.key === key) || {
              label: labels.get(key),
              value: '',
              key,
              id: getNextUuid(),
              editable: true,
              locked: false,
              added: true,
              dispatches: [],
            }
        )
      )
    }

    removedKeys.forEach((key) =>
      trackEvent({ event_name: 'click_action', link_name: `additional-facts-fact-${key}-removed` })
    )
    addedKeys.forEach((key) =>
      trackEvent({ event_name: 'click_action', link_name: `additional-facts-fact-${key}-added` })
    )
  }

  return (
    <AddRemoveFieldsDialog
      overlay={overlay}
      title={t('additional-facts-card.add-remove-dialog.title', 'Add/Remove Additional Facts')}
      filterLabel={t('additional-facts-card.add-remove-dialog.filter', 'Filter Additional Facts')}
      groups={groups}
      initialChecked={initialChecked}
      onSave={handleSave}
      linkNamePrefix="additional-facts-dialog"
      testIdPrefix="additional-facts"
    />
  )
}

AdditionalFactsDialog.propTypes = {
  overlay: PropTypes.shape({ isOpen: PropTypes.bool, close: PropTypes.func }).isRequired,
  factOptions: PropTypes.shape({
    commonFacts: PropTypes.arrayOf(factOptionShape).isRequired,
    otherFacts: PropTypes.arrayOf(factOptionShape).isRequired,
  }).isRequired,
  fieldArrayMethods: PropTypes.shape({ remove: PropTypes.func, append: PropTypes.func }).isRequired,
}

export default AdditionalFactsDialog
